import { v4 as uuidv4 } from 'uuid';

import useTranslation from 'components/Map/hooks/useTranslation';

import {
    BOUNDARY_PART_REQUEST_BOUNDARY_TYPE,
    BOUNDARY_TYPE_MAP,
    GEOJSON_FEATURE_TYPE,
    GEOJSON_PROPERTY,
    GEOJSON_PROPERTY_MAP,
    GEOMETRY_TYPE_MAP
} from './constants';
import type {
    BoundaryPartData,
    CreateBoundaryRequest,
    Feature,
    GeoJSONPolygonType,
    Polygon,
    PolygonDTO,
    Position
} from './types';

const useDTO = () => {
    const { BOUNDARY, NO_APPLY, NO_ENTRY } = useTranslation();

    const convertToDTO = (
        boundary: Feature<Polygon>,
        obstacles?: Feature<Polygon>[]
    ): CreateBoundaryRequest => {
        const boundaryPart = convertBoundaryPart(boundary);
        const obstacleParts = obstacles?.map(convertObstaclePart) || [];
        const name = boundary.properties?.[GEOJSON_PROPERTY.NAME];

        return { boundaryParts: [boundaryPart, ...obstacleParts], name };
    };

    const BOUNDARY_NAME_MAP = {
        [BOUNDARY_PART_REQUEST_BOUNDARY_TYPE.APPLY]: BOUNDARY,
        [BOUNDARY_PART_REQUEST_BOUNDARY_TYPE.NO_APPLY]: NO_APPLY,
        [BOUNDARY_PART_REQUEST_BOUNDARY_TYPE.NO_ENTRY]: NO_ENTRY
    };

    const convertBoundaryPart = (boundary: Feature<Polygon>): BoundaryPartData => ({
        boundary: boundary.geometry as PolygonDTO,
        boundaryType: BOUNDARY_PART_REQUEST_BOUNDARY_TYPE.APPLY,
        geometryType:
            GEOMETRY_TYPE_MAP[
                boundary.properties?.[GEOJSON_PROPERTY.POLYGON_TYPE] as GeoJSONPolygonType
            ]
    });

    const convertObstaclePart = (obstacle: Feature<Polygon>): BoundaryPartData => ({
        boundary: obstacle.geometry as PolygonDTO,
        boundaryType: BOUNDARY_PART_REQUEST_BOUNDARY_TYPE.NO_ENTRY,
        geometryType:
            GEOMETRY_TYPE_MAP[
                obstacle.properties?.[GEOJSON_PROPERTY.POLYGON_TYPE] as GeoJSONPolygonType
            ]
    });

    const convertFromDTO = ({
        applicationId,
        applicationName,
        boundary,
        boundaryType,
        geometryType
    }: BoundaryPartData): Feature<Polygon> => {
        const { coordinates } = boundary;
        const isApply = boundaryType === BOUNDARY_PART_REQUEST_BOUNDARY_TYPE.APPLY;
        const displayName = isApply ? applicationName || '' : BOUNDARY_NAME_MAP[boundaryType];

        return {
            geometry: {
                coordinates: coordinates as Position[][],
                type: GEOJSON_FEATURE_TYPE.POLYGON
            },
            id: uuidv4(),
            properties: {
                [GEOJSON_PROPERTY.POLYGON_TYPE]: GEOJSON_PROPERTY_MAP[geometryType],
                [GEOJSON_PROPERTY.BOUNDARY_TYPE]: BOUNDARY_TYPE_MAP[boundaryType],
                [GEOJSON_PROPERTY.NAME]: displayName,
                [GEOJSON_PROPERTY.EXT_APPLICATION_ID]: applicationId
            },
            type: 'Feature'
        };
    };

    const convertFromDTOs = (parts: BoundaryPartData[]): Feature<Polygon>[] =>
        parts.map(convertFromDTO);

    return {
        convertFromDTOs,
        convertToDTO
    };
};

export default useDTO;
